/**
 * Registro de acesso às telas que não tinham rastreamento (JOR4-7826).
 *
 * As lacunas vêm do levantamento do JOR4-7670; cada entrada corresponde a uma
 * tarefa filha da história (RN01). O registro é feito no `incluiTab` do
 * `ControleTabPadrao`, por onde passa toda abertura de aba — menu, acesso
 * rápido, últimos acessos e CTAs. Não fica no construtor de cada tela de
 * propósito: Escalas também nasce embutida em Empresas, e Motoristas e
 * Veículos também nascem dentro de popups do Controle da Jornada; no
 * construtor, esses usos seriam contados como acesso à tela.
 *
 * Só entram aqui telas que não registram o próprio acesso. O Controle da
 * Jornada e a Assinatura Eletrônica já registram na criação, e incluí-las
 * contaria cada acesso duas vezes.
 */

import { registrarUso } from "./registrarUso";
import { TELAS, type ItemRastreado } from "./telas";

/** Chave: o nome da classe em `importTelas` (TLA_CLASSEGRID). */
export const TELAS_RASTREADAS: Readonly<Record<string, ItemRastreado>> = {
    // Jornada
    // Não era lacuna do JOR4-7670 e a 7872 trata das funções dela, mas sem isto
    // a Jornada Assistida seria a única tela sem registro de acesso.
    JornadaAssistida: TELAS.jornadaAssistida,
    Dashboards: TELAS.dashboards,
    TelaPaineisBIJornada: TELAS.paineisBi,
    TelaVisaoBIJornada: TELAS.visaoBi,

    // Cadastros
    GridEmpresasJornada: TELAS.empresas,
    GridMotoristasJornada: TELAS.motoristas,
    GridMotoristasTecnologia: TELAS.motoristasTecnologia,
    GridVeiculosJornada: TELAS.veiculos,
    GridEscalasJornada: TELAS.escalas,
    CadastroArvoreDecisao: TELAS.arvoreDecisao,
    GridServicosJornada: TELAS.servicos,
    NovidadesSistema: TELAS.novidades,
    CadastroEmbarcadores: TELAS.embarcadores,
    TelaCadastroBIJornada: TELAS.cadastroBi,
    SystemIndicatorsManagement: TELAS.indicadores,
    IndicatorSectionsManagement: TELAS.secoesIndicadores,

    // Configurações
    ChatAssistenteIA: TELAS.chatAssistenteIa,
    TelasSistema: TELAS.telasSistema,
    MenusSistema: TELAS.menusSistema,
    UsuariosSistema: TELAS.usuariosSistema,
    PlanosSistema: TELAS.planosSistema,
    VinculosEmpresaPlano: TELAS.vinculosEmpresaPlano,
    PermissoesSistema: TELAS.permissoesSistema,
    PermissoesSistemaPorUsuario: TELAS.permissoesPorUsuario,
    GruposEmpresas: TELAS.gruposEmpresas,
    VinculosEmbarcadoraPlano: TELAS.vinculosEmbarcadoraPlano,
    ConfiguracoesMensagensAutomaticas: TELAS.mensagensAutomaticas,

    // Relatórios
    GridListaDeRelatorios: TELAS.listaRelatorios,
    // As três versões do Cartão Ponto Sintético registram cada consulta como
    // função (`fetchData`); o acesso à tela é aqui. A Python e a antiga não
    // eram lacuna do JOR4-7670 — contavam a consulta como acesso —, mas no
    // e360 a consulta virou função e a abertura precisa do próprio registro.
    CartaoPontoSintetico: TELAS.cartaoPontoSintetico,
    CartaoPontoSinteticoGo: TELAS.cartaoPontoSinteticoGo,
    CartaoPontoSinteticoAntigo: TELAS.cartaoPontoSinteticoAntigo,
    // Mesmo caso: no Release o "carregar"/"gerar relatório" valia como acesso;
    // no e360 ele é função, e a abertura da tela registra aqui.
    ComprovantesRegistroPonto: TELAS.comprovantesRegistroPonto,
    RelatorioAlteracoesManuaisEventos: TELAS.relatorioAlteracoesManuais,
    RelatorioDSR: TELAS.relatorioDsr,
    RelatorioProdutividade: TELAS.relatorioProdutividade,
    RelatorioKmDiariosMotoristas: TELAS.relatorioKmDiariosMotoristas,
    RelatorioFaltaDescansoRepouso: TELAS.relatorioFaltaDescansoRepouso,
    RelatorioHorasFalta: TELAS.relatorioHorasFalta,
    RelatorioEventos: TELAS.relatorioEventos,
    RelatorioOcorrencias: TELAS.relatorioOcorrencias,
    RelatorioAnomalias: TELAS.relatorioAnomalias,
    RelatorioAfastamentos: TELAS.relatorioAfastamentos,
    RelatorioAcessos: TELAS.relatorioAcessos,
    RelatorioAtivacaoMotoristas: TELAS.relatorioAtivacaoMotoristas,
    RelatorioNaoConformidades: TELAS.relatorioNaoConformidades,
    RelatorioAlteracoesViaMobile: TELAS.relatorioAlteracoesViaMobile,
    RelatorioDiarias: TELAS.relatorioDiarias,
    RelatorioMensagensJornada: TELAS.relatorioMensagensJornada,
    RelatorioBancoDeHoras: TELAS.relatorioBancoDeHoras,
    RelatorioExcecoes: TELAS.relatorioExcecoes,
    RelatorioTratativaExcecoes: TELAS.relatorioTratativaExcecoes,
    RelatorioAnotacoesJornada: TELAS.relatorioAnotacoesJornada,
    RelatorioLogsAplicativo: TELAS.relatorioLogsAplicativo,

    // Jornada de Embarcadores
    GestaoViagensJornadaTerceiros: TELAS.gestaoViagensTerceiros,
    ControleJornadaTerceirosTab: TELAS.controleJornadaTerceiros,
    ControlePontuacaoPenalidadeTab: TELAS.pontuacaoPenalidadeTerceiros,
    ManutencaoJornadaTerceiros: TELAS.manutencaoJornadaTerceiros,
    RelatorioNaoConformidadesTerceiros: TELAS.relatorioNaoConformidadesTerceiros,
    RelatorioJornadaTerceiros: TELAS.relatorioJornadaTerceiros,
};

/**
 * O mesmo assistente, aberto pelo botão flutuante do `ControleTabPadrao` —
 * um popup que não passa pelo `incluiTab`. Vai como função da tela do chat
 * para somar com ela e ainda assim distinguir a porta de entrada.
 */
export const registrarAcessoChatFlutuante = (): void => {
    registrarUso(TELAS.chatAssistenteIa, {
        codigo: "botao_flutuante",
        nome: "Aberto pelo botão flutuante",
    });
};

/** Registra o acesso à tela, se ela estiver entre as rastreadas aqui. */
export const registrarAcessoTela = (classe: string): void => {
    // Só as chaves próprias: `classe` vem de fora e um "toString" não pode
    // cair no protótipo.
    if (!Object.prototype.hasOwnProperty.call(TELAS_RASTREADAS, classe)) {
        return;
    }

    registrarUso(TELAS_RASTREADAS[classe]);
};
